// Groups catalog components by importable package and, within a package, by CATEGORY.
//
// Catalog v2 carries the grouping explicitly: each package declares `categories`
// ([{ id, label, order }]) and each component has `category` (id) + `order`. The view
// attaches the resolved category label/order to each component (categoryId/categoryLabel/
// categoryOrder); this module only buckets + sorts. It falls back to a family derived from
// the entry path when category metadata is absent (older catalogs / local fixtures).

const FAMILY_LABELS = {
  nivoRocks: 'Nivo charts',
  tremor: 'Tremor',
  'ag-grid': 'Data grid (ag-Grid)',
};

function titleCase(value) {
  return value.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Derive a fallback family ({ key, label }) from a component's entry path.
 *
 * Used only when the catalog provides no explicit category for the component.
 *
 * @param {object} component - Normalized catalog component (must have `entry`).
 * @returns {{ key: string, label: string }} Family key and human label.
 */
export function deriveFamily(component) {
  const match = /^\/lib\/([^/]+)\//.exec(component.entry || '');
  const key = match ? match[1] : 'other';
  return { key, label: FAMILY_LABELS[key] || titleCase(key) };
}

/**
 * Group components by package, then by category (catalog metadata, else derived family).
 *
 * Categories are ordered by their catalog `order`; components within a category by their
 * own `order`, then name. Output shape is stable for the nav: families === categories.
 *
 * @param {Array<object>} components - Components carrying package + (optional) category metadata.
 * @returns {Array<{package: string, families: Array<{key: string, label: string, order: number, items: Array<object>}>}>}
 */
export function groupCatalogComponents(components) {
  const byPackage = new Map();

  components.forEach(component => {
    const pkg = component.package || 'unknown';
    if (!byPackage.has(pkg)) byPackage.set(pkg, new Map());
    const categories = byPackage.get(pkg);

    const fallback = deriveFamily(component);
    const key = component.categoryId || fallback.key;
    const label = component.categoryLabel || fallback.label;
    const order = component.categoryOrder ?? 999;

    if (!categories.has(key)) categories.set(key, { key, label, order, items: [] });
    categories.get(key).items.push(component);
  });

  return Array.from(byPackage.keys())
    .sort()
    .map(pkg => ({
      package: pkg,
      families: Array.from(byPackage.get(pkg).values())
        .map(category => ({
          ...category,
          items: category.items
            .slice()
            .sort(
              (a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name)
            ),
        }))
        .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label)),
    }));
}
